import axios from 'axios';
import { STORAGE_KEYS } from '../constants';

// Create axios instance with default config
const api = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001',
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service class
class ApiService {
  // Generic GET request
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await api.get<T>(url, { params });
    return response.data;
  }

  // Generic POST request
  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await api.post<T>(url, data);
    return response.data;
  }

  // Generic PUT request
  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await api.put<T>(url, data);
    return response.data;
  }

  // Generic PATCH request
  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await api.patch<T>(url, data);
    return response.data;
  }

  // Generic DELETE request
  async delete<T = any>(url: string): Promise<T> {
    const response = await api.delete<T>(url);
    return response.data;
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Type definitions for better type safety
interface PasswordChangeData {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
}

// Specific API methods for different resources
export const authAPI = {
  login: (email: string, password: string) => 
    apiService.post<{ token: string; user: any }>('/api/auth/login', { email, password }),
  
  getProfile: () => 
    apiService.get<any>('/api/auth/profile'),
  
  updateProfile: (data: any) => 
    apiService.put<any>('/api/auth/profile', data),
  
  changePassword: (data: PasswordChangeData) => 
    apiService.put<any>('/api/auth/change-password', data),
  
  changeUserPassword: (userId: string, data: PasswordChangeData) => 
    apiService.put<any>(`/api/auth/change-user-password/${userId}`, data),
};

export const employeesAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/employees'),
  
  getActive: () => 
    apiService.get<any[]>('/api/employees?active=true'),
  
  getTeam: () => 
    apiService.get<any[]>('/api/employees/team'),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/employees/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/employees', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/employees/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/employees/${id}`),
};

export const clientsAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/clients'),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/clients/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/clients', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/clients/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/clients/${id}`),
};

export const projectsAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/projects'),
  
  getMyProjects: () => 
    apiService.get<any[]>('/api/projects/my-projects'),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/projects/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/projects', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/projects/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/projects/${id}`),
};

export const invoicesAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/invoices'),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/invoices/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/invoices', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/invoices/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/invoices/${id}`),
  
  generatePDF: (id: string) => 
    apiService.get<any>(`/api/invoices/${id}/pdf`),
  
  generatePDFBlob: async (id: string) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const response = await api.get(`/api/invoices/${id}/pdf`, {
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },
};

export const timesheetsAPI = {
  getAll: (params?: any) => 
    apiService.get<any[]>('/api/timesheets', params),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/timesheets/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/timesheets', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/timesheets/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/timesheets/${id}`),
};

export const timeOffAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/time-off'),
  
  getById: (id: string) => 
    apiService.get<any>(`/api/time-off/${id}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/time-off', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/time-off/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/time-off/${id}`),
  
  getCalendar: () => 
    apiService.get<any[]>('/api/time-off/calendar'),
  
  getBalance: (employeeId?: string) => 
    apiService.get<any>(`/api/time-off/balance${employeeId ? `/${employeeId}` : ''}`),
  
  getBalances: () => 
    apiService.get<any[]>('/api/time-off/balances'),
  
  updateBalance: (employeeId: string, data: any) => 
    apiService.put<any>(`/api/time-off/balance/${employeeId}`, data),
  
  getPendingCount: () => 
    apiService.get<{ count: number }>('/api/time-off/pending-count'),
  
  getUpcoming: () => 
    apiService.get<any[]>('/api/time-off/upcoming'),
};

export const holidaysAPI = {
  getAll: (year?: number) => 
    apiService.get<any[]>('/api/holidays', typeof year === 'number' ? { year } : undefined),
  
  getByYear: (year: number) => 
    apiService.get<any[]>(`/api/holidays/year/${year}`),
  
  create: (data: any) => 
    apiService.post<any>('/api/holidays', data),
  
  update: (id: string, data: any) => 
    apiService.put<any>(`/api/holidays/${id}`, data),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/holidays/${id}`),
};

export const filesAPI = {
  upload: (file: File, entityType: string, entityId: string, originalName?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);
    if (originalName) {
      formData.append('originalName', originalName);
    }
    return apiService.post<any>('/api/files/upload', formData);
  },
  
  download: (fileId: string) => 
    apiService.get<any>(`/api/files/download/${fileId}`),
  
  getByEntity: (entityType: string, entityId: string) => 
    apiService.get<any[]>(`/api/files/${entityType}/${entityId}`),
  
  delete: (fileId: string) => 
    apiService.delete<any>(`/api/files/${fileId}`),
  
  uploadProfilePicture: (file: File, entityType: string, entityId: string) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);
    formData.append('originalName', file.name);
    return apiService.post<any>('/api/files/profile-picture', formData);
  },
};

export const technologiesAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/technologies'),
};

export const reportsAPI = {
  getEmployeeReport: (params?: any) => 
    apiService.get<any>('/api/reports/employees', params),
  
  getProjectReport: (params?: any) => 
    apiService.get<any>('/api/reports/projects', params),
  
  getTimesheetReport: (params?: any) => 
    apiService.get<any>('/api/reports/timesheets', params),
};

export const welcomePageAPI = {
  get: () => 
    apiService.get<any>('/api/welcome-page'),
  
  update: (data: any) => 
    apiService.put<any>('/api/welcome-page', data),
};

export const fitpassAPI = {
  getAll: () => 
    apiService.get<any[]>('/api/fitpass'),
  
  
  create: (data: any) => 
    apiService.post<any>('/api/fitpass', data),

  update: (id: string, data: any) =>
    apiService.put<any>(`/api/fitpass/${id}`, data),

  copyToNextMonth: (payload: {
    year: number;
    month: number;
    timezoneOffset: number;
  }) =>
    apiService.post<{ copied: number; skipped: number; targetMonth: string }>(
      '/api/fitpass/copy-to-next-month',
      payload,
    ),
  
  updatePaid: (id: string, paid: boolean) =>
    apiService.patch<any>(`/api/fitpass/${id}/paid`, { paid }),
  
  updateAdditionalPersonPaid: (id: string, personIndex: number, paid: boolean) =>
    apiService.patch<any>(`/api/fitpass/${id}/additional-person/${personIndex}/paid`, { paid }),
  
  delete: (id: string) => 
    apiService.delete<any>(`/api/fitpass/${id}`),
};

export const lunchAPI = {
  // Menu endpoints
  getMenu: (date: string) => 
    apiService.get<any>(`/api/lunch/menu/${date}`),
  
  getTodayMenu: () => 
    apiService.get<any>('/api/lunch/menu/today'),
  
  createMenu: (data: { date: string; rawText: string }) => 
    apiService.post<any>('/api/lunch/menu', data),
  
  // Order endpoints
  getMyOrder: (date: string) => 
    apiService.get<any>(`/api/lunch/order/${date}`),
  
  createOrder: (data: { date: string; itemIds: string[]; totalPrice?: number; note?: string }) => 
    apiService.post<any>('/api/lunch/order', data),
  
  deleteOrder: (date: string) => 
    apiService.delete<any>(`/api/lunch/order/${date}`),
  
  // Employee names (non-sensitive data only)
  getEmployeeNames: () => 
    apiService.get<any[]>('/api/lunch/employee-names'),
  
  // Order management
  getOrders: (date: string) => 
    apiService.get<any[]>(`/api/lunch/orders/${date}`),
  
  getSummary: (year: number, month: number) => 
    apiService.get<any[]>(`/api/lunch/summary/${year}/${month}`),
  
  updatePaymentPaid: (employeeId: string, year: number, month: number, paid: boolean) =>
    apiService.patch<any>(`/api/lunch/payment/${employeeId}/${year}/${month}`, { paid }),
  
  getEmployeeOrder: (employeeId: string, date: string) => 
    apiService.get<any>(`/api/lunch/employee-order/${employeeId}/${date}`),
  
  createAdminOrder: (data: { employeeId: string; date: string; itemIds: string[]; totalPrice?: number; note?: string }) => 
    apiService.post<any>('/api/lunch/admin-order', data),
  
  deleteAdminOrder: (employeeId: string, date: string) => 
    apiService.delete<any>(`/api/lunch/admin-order/${employeeId}/${date}`),
};

export const notesAPI = {
  get: () => 
    apiService.get<any>('/api/notes'),
  
  update: (data: { content: string }) => 
    apiService.put<any>('/api/notes', data),
};

export default apiService;
